<template>
  <div class="card" :class="cssClass">
    <div class="card-body">
      <div class="lp-mortgage-calculator">
        <div class="row g-lg-4">
          <div class="col-md-6">
            <div class="mb-3 mb-lg-4">
              <base-group-input :required-flag="true" inputLabel="House Cost" front-group="$">
                <base-input
                  label="House Cost"
                  v-model.number="houseCost"
                  type="number"
                  placeholder="200000"
                  :show-label="false"
                />
              </base-group-input>
            </div>
            <div class="mb-3 mb-lg-4 mortgage-calculator__inline-inputs">
              <base-group-input :required-flag="true" inputLabel="Down Payment" front-group="$">
                <base-input
                  label="Down Payment"
                  :showLabel="false"
                  v-model.number="downPaymentNumberDisplay"
                  type="number"
                />
              </base-group-input>
              <span class="mortgage-calculator-inline-inputs__middle">or</span>
              <base-group-input back-group="%">
                <base-input
                  v-model="downPaymentPercDisplay"
                  type="number"
                  :showLabel="false"
                  label="Down payment percentage"
                />
              </base-group-input>
            </div>
            <div class="mb-3 mb-lg-4">
              <label for="`input-${inputId}`" class="form-label">
                Mortgage Term
                <span>*</span>
              </label>
              <select
                class="form-select"
                aria-label="Mortgage Term"
                v-model.number="mortgageTerm"
              >
                <option value="30">30 years</option>
                <option value="20">20 years</option>
                <option value="15">15 years</option>
                <option value="10">10 years</option>
              </select>
            </div>
            <div class="mb-3 mb-lg-4">
              <base-group-input :required-flag="true" inputLabel="Interest Rate" back-group="%">
                <base-input
                  label="Interest Rate"
                  :showLabel="false"
                  v-model.number="interestRate"
                  type="number"
                  step=".01"
                  placeholder="3.25"
                />
              </base-group-input>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <base-group-input
                inputLabel="Estimated Monthly Payment"
                front-group="$"
              >
                <base-input
                  label="Estimated Monthly Payment"
                  v-model.number="estMonthlyPayment"
                  :showLabel="false"
                  type="number"
                  disabled
                />
              </base-group-input>
            </div>
            <div class="mortage-calulator__chart">
              <canvas :id="`mortgage-calculator-${calculatorId}`"></canvas>
            </div>
            <div>
              <div class="calculator__result-row">
                <span class="calculator-result-row__dot bg-primary"></span>
                <p class="calculator-result-row__label">
                  Principal &amp; Interest
                </p>
                <p class="calculator-result-row__number">
                  <span>{{ formatCurrency(principalAndInterest) }}</span>
                </p>
              </div>
              <div class="calculator__result-row">
                <span class="calculator-result-row__dot bg-secondary"></span>
                <p class="calculator-result-row__label">
                  Homeowner's Insurance
                </p>
                <div class="calculator-result-row__number">
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input
                      class="form-control"
                      type="number"
                      v-model.number="homeInsurance"
                      aria-label="Homeowner's Insurance"
                    />
                  </div>
                </div>
              </div>
              <div class="calculator__result-row">
                <span class="calculator-result-row__dot bg-success"></span>
                <p class="calculator-result-row__label">Property Tax</p>
                <div class="calculator-result-row__number">
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input
                      class="form-control"
                      type="number"
                      v-model.number="propertyTax"
                      aria-label="Property Tax"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
      </div>
      <slot name="cta"></slot>
    </div>
  </div>
  <div class="lp-calculator-disclaimer">
    <slot name="disclaimer"></slot>
  </div>
</template>

<script lang="ts">
declare const DataIntelligenceSubmitScript: any;

import {
  ArcElement,
  Chart,
  ChartConfiguration,
  ChartData,
  PieController,
} from 'chart.js';
import { v4 as uuid } from 'uuid';
import { defineComponent, computed, onMounted, ref, watch } from 'vue';

import { formatCurrency } from '../helpers/calculators/formatting';
import { debounce } from '../helpers/calculators/input';
import { sendInsightReport } from '../helpers/calculators/insight';

import BaseInput from './BaseInput.vue';
import BaseGroupInput from './BaseGroupInput.vue';

export default defineComponent({
  name: 'MortgageCalculator',
  components: {
    BaseInput,
    BaseGroupInput,
  },
  props: {
    cssClass: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup() {
    const calculatorId = uuid();

    // Principal
    const annualRate = ref(true);
    const houseCost = ref(200000);
    watch(houseCost, () => {
      setDownpaymentNumber();
    });
    const downPaymentNumber = ref(10000);
    const setDownpaymentNumber = () => {
      downPaymentNumber.value = Math.round(
        houseCost.value * (downPaymentPercentage.value / 100),
      );
    };
    const downPaymentNumberDisplay = computed({
      get: () =>
        Math.round(houseCost.value * (downPaymentPercentage.value / 100)),
      set: (value) => {
        downPaymentNumber.value = value;
      },
    });
    const downPaymentPercentage = ref(5);
    const downPaymentPercDisplay = computed({
      get: () => {
        return +((downPaymentNumber.value / houseCost.value) * 100).toFixed(3);
      },
      set: (value) => {
        downPaymentPercentage.value = value;
        setDownpaymentNumber();
      },
    });
    const loanAmount = computed(
      () => houseCost.value - downPaymentNumber.value,
    );

    // Interest
    const interestRate = ref(3.5);
    const calculatedRate = computed(() => {
      return annualRate.value
        ? interestRate.value / 12 / 100
        : interestRate.value / 100;
    });

    // Principal and Interest
    const mortgageTerm = ref(30);
    const mortgageTermMonths = computed(() => mortgageTerm.value * 12);
    const principalAndInterest = computed(() => {
      const value =
        (calculatedRate.value *
          loanAmount.value *
          (1 + calculatedRate.value) ** mortgageTermMonths.value) /
        ((1 + calculatedRate.value) ** mortgageTermMonths.value - 1);

      if (isNaN(value)) {
        return 0;
      }

      return value;
    });
    watch(principalAndInterest, () => {
      updatePaymentValues();
      updateChart();
    });

    // Insurance
    // * Insurance is estimated at 3.5% of the loan amount
    // TODO: Add PMI option
    const getHomeInsuranceValue = () =>
      Math.round((loanAmount.value * 0.0035) / 12);
    const homeInsurance = ref(getHomeInsuranceValue());

    // Taxes
    // * Tax is estimated at 1.25% of the loan amount
    const getPropertyTax = () => Math.round((loanAmount.value * 0.0125) / 12);
    const propertyTax = ref(getPropertyTax());

    const updatePaymentValues = () => {
      homeInsurance.value = getHomeInsuranceValue();
      propertyTax.value = getPropertyTax();
    };
    watch([homeInsurance, propertyTax], () => {
      updateChart();
    });

    // Payment
    const estMonthlyPayment = computed(() =>
      Math.round(
        principalAndInterest.value + homeInsurance.value + propertyTax.value,
      ),
    );

    // Chart
    let chartContext: CanvasRenderingContext2D | null = null;
    let chart: Chart | undefined = undefined;
    const chartData: ChartData = {
      datasets: [
        {
          label: 'Payment Information',
          data: [
            principalAndInterest.value,
            homeInsurance.value,
            propertyTax.value,
          ],
          // * These colors must be updated manually to match their bubbles
          backgroundColor: ['#0d6efd', '#adb5bd', '#198754'],
          hoverOffset: 3,
        },
      ],
    };
    const chartConfig: ChartConfiguration = {
      type: 'pie',
      data: chartData,
    };
    const initChart = () => {
      Chart.register(ArcElement, PieController);
      const chartCanvasElement = document.getElementById(
        `mortgage-calculator-${calculatorId}`,
      ) as HTMLCanvasElement;

      if (chartCanvasElement) {
        chartContext = chartCanvasElement.getContext('2d');

        if (chartContext) {
          chart = new Chart(chartContext, chartConfig);
        }
      }
    };
    const updateChart = () => {
      if (chart) {
        chart.data.datasets[0].data = [
          principalAndInterest.value,
          homeInsurance.value,
          propertyTax.value,
        ];
        chart.update();
      }
    };

    // Sitefinity Insight
    const reportInputToDEC = () => {
      DataIntelligenceSubmitScript._client.sentenceClient.writeSentence({
        predicate: 'Input',
        object: 'Mortgage Calculator',
      });
    };
    watch(
      estMonthlyPayment,
      debounce(() => sendInsightReport(reportInputToDEC)),
    );

    onMounted(() => {
      initChart();
    });

    return {
      calculatorId,
      downPaymentNumber,
      downPaymentNumberDisplay,
      downPaymentPercentage,
      downPaymentPercDisplay,
      estMonthlyPayment,
      formatCurrency,
      homeInsurance,
      houseCost,
      interestRate,
      mortgageTerm,
      principalAndInterest,
      propertyTax,
    };
  },
});
</script>

<style scoped></style>
